// @flow

import * as React from 'react';

import { Info } from 'lucide-react';
import { Link } from 'react-router-dom';

import { selectCartUUID, type ProductId } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Badge, Base, Button, Text } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { useFeatureFlags } from 'src/hooks/useFeatureFlag';

import { ListRow } from '../../../../ListRow';

import LabelContent from './LabelContent';
import Price from './Price';

export type Props = {
  productId: ProductId,
  authCode?: string,
  hasTaxes?: boolean,
  /** normal (crossed out) price in case of discount */
  priceCrossedOut?: number,
  /** total is total price */
  total: void | number,
};

export default function CodeRow({
  productId,
  authCode,
  hasTaxes,
  priceCrossedOut,
  total,
}: Props): React.MixedElement {
  const { FF_CART_OVERVIEW_NDS } = useFeatureFlags();

  const { __ } = useTranslator();
  const cartId = useAppSelector(selectCartUUID);

  if (FF_CART_OVERVIEW_NDS) {
    const listRowProps = authCode
      ? {
          cta: (
            <Button
              kind="link"
              type="button"
              to={`/cart/${cartId}/configure/${productId}/authinfo`}
            >
              {__('Change')}
            </Button>
          ),
          intent: 'muted',
        }
      : {
          cta: (
            <Button
              kind="link"
              type="button"
              to={`/cart/${cartId}/configure/${productId}/authinfo`}
            >
              {__('Add')}
            </Button>
          ),
          icon: <Info size={20} />,
          intent: 'warning',
        };

    return (
      <ListRow {...listRowProps}>
        {authCode
          ? __('Authorization Code: %(authCode)s', { authCode })
          : __('Missing authorization code')}
      </ListRow>
    );
  }

  // Legacy style
  return (
    <Base flex flexShrink0 gap="xsmall" marginBottom="xsmall">
      <LabelContent label={__('Authorization Code')}>
        {authCode ? (
          <>
            <Text size="small">{authCode}</Text>
            <Link to={`/cart/${cartId}/configure/${productId}/authinfo`}>
              {__('Change')}
            </Link>
          </>
        ) : (
          <>
            <Badge intent="warning">{__('Missing')}</Badge>
            <Link to={`/cart/${cartId}/configure/${productId}/authinfo`}>
              {__('Add')}
            </Link>
          </>
        )}
      </LabelContent>
      <Base tagName="span" flexExpandLeft>
        <Price
          price={total}
          priceCrossedOut={priceCrossedOut}
          showExcludeVatMention={hasTaxes}
        />
      </Base>
    </Base>
  );
}
